import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { itemApi } from '../../settings/items/api/itemApi';
import { Item } from '../../settings/items/types/item.types';
import { inventoryLocationsApi } from '../api/locationsApi';
import { itemLocationApi } from '../../settings/items/api/itemLocationApi';
import { ItemLocation as ApiItemLocation } from '../../settings/items/types/item-location.types';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { hospitalApi } from '../../../api/hospitalApi';
import { format } from 'date-fns';

interface Location {
  id: string;
  name: string;
}

// Define our own ItemLocation interface that matches what we need
interface ItemLocation {
  id: string;
  itemId: string;
  locationId: string;
  location?: Location;
  item?: Item;
  batchNumber: string;
  expiryDate?: string | null;
  quantity: number; // This maps to balance in the API
  reorderLevel?: number;
  lastUpdated?: string;
  // Add any other fields we need
  [key: string]: any;
}

interface StockItem {
  id: string;
  itemCode: string;
  itemName: string;
  itemGroup: string;
  unit: string;
  unitOfMeasure: string;
  locations: ItemLocation[];
  totalQuantity: number;
  reorderLevel?: number;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
  lastUpdated: string;
}

const StockBalanceReport: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [locationFilter, setLocationFilter] = useState<string>('all');
  const [itemGroupFilter, setItemGroupFilter] = useState<string>('All Groups');
  const [itemFilter, setItemFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [locations, setLocations] = useState<Location[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [itemLocations, setItemLocations] = useState<ItemLocation[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [itemGroups, setItemGroups] = useState<string[]>(['All Groups']);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  // Load data on component mount
  useEffect(() => {
    loadLocations();
    loadItems();
    loadHospitalInfo();
  }, []);

  // Load hospital info for reports
  const loadHospitalInfo = async () => {
    try {
      const info = await hospitalApi.getCompanyProfile();
      setHospitalInfo(info);
    } catch (error) {
      console.error('Error loading hospital info:', error);
    }
  };

  // Load locations
  const loadLocations = async () => {
    try {
      const data = await inventoryLocationsApi.getAllActiveLocations();
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
      enqueueSnackbar('Failed to load locations', { variant: 'error' });
    }
  };

  // Load items
  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await itemApi.getItems({ limit: 1000, page: 1 });
      const itemsData = response.data || [];
      setItems(itemsData);
      
      // Extract unique item groups
      const groups = ['All Groups', ...new Set(itemsData.map(item => item.itemGroup))];
      setItemGroups(groups);
      
      // Load item locations after items are loaded
      await loadItemLocations();
    } catch (error) {
      console.error('Error loading items:', error);
      enqueueSnackbar('Failed to load items', { variant: 'error' });
      setLoading(false);
    }
  };

  // Load item locations
  const loadItemLocations = async () => {
    try {
      // Get all item locations from the API
      const apiItemLocations = await itemLocationApi.getItemLocations({
        includeItem: true,
        includeLocation: true
      });
      
      // Convert API ItemLocation to our ItemLocation format
      const mappedItemLocations: ItemLocation[] = apiItemLocations.map(loc => ({
        id: loc.id,
        itemId: loc.itemId,
        locationId: loc.locationId,
        location: loc.location,
        item: loc.item,
        batchNumber: loc.batchNumber,
        expiryDate: loc.expiryDate,
        quantity: loc.balance, // Map balance to quantity
        reorderLevel: loc.item?.reorderLevel || undefined,
        lastUpdated: loc.updatedAt
      }));
      
      setItemLocations(mappedItemLocations);
      
      // Process the data to create stock items
      processStockItems(mappedItemLocations);
    } catch (error) {
      console.error('Error loading item locations:', error);
      enqueueSnackbar('Failed to load stock data', { variant: 'error' });
      setLoading(false);
    }
  };

  // Process item locations to create stock items
  const processStockItems = (itemLocationsData: ItemLocation[]) => {
    // Group by item ID
    const itemGroups: Record<string, ItemLocation[]> = {};
    
    itemLocationsData.forEach(itemLoc => {
      if (!itemGroups[itemLoc.itemId]) {
        itemGroups[itemLoc.itemId] = [];
      }
      itemGroups[itemLoc.itemId].push(itemLoc);
    });
    
    // Create stock items
    const stockItemsData: StockItem[] = [];
    
    Object.keys(itemGroups).forEach(itemId => {
      const itemLocations = itemGroups[itemId];
      const item = items.find(i => i.id === itemId);
      
      if (item) {
        // Calculate total quantity
        const totalQuantity = itemLocations.reduce((sum, loc) => sum + loc.quantity, 0);
        
        // Determine status
        let status: 'In Stock' | 'Low Stock' | 'Out of Stock' = 'Out of Stock';
        if (totalQuantity > 0) {
          // Check if any location is below reorder level
          const anyBelowReorderLevel = itemLocations.some(loc => 
            loc.reorderLevel && loc.quantity <= loc.reorderLevel && loc.quantity > 0
          );
          status = anyBelowReorderLevel ? 'Low Stock' : 'In Stock';
        }
        
        // Create stock item
        stockItemsData.push({
          id: item.id,
          itemCode: item.itemCode,
          itemName: item.itemName,
          itemGroup: item.itemGroup,
          unit: item.unitOfMeasure,
          unitOfMeasure: item.unitOfMeasure,
          locations: itemLocations,
          totalQuantity,
          reorderLevel: item.reorderLevel || undefined,
          status,
          lastUpdated: new Date().toISOString()
        });
      }
    });
    
    setStockItems(stockItemsData);
    setLoading(false);
  };

  // Filter items based on selected filters
  const filteredItems = stockItems.filter(item => {
    // Filter by location
    const matchesLocation = locationFilter === 'all' || 
      item.locations.some(loc => loc.locationId === locationFilter);
    
    // Filter by item group
    const matchesItemGroup = itemGroupFilter === 'All Groups' || 
      item.itemGroup === itemGroupFilter;
    
    // Filter by specific item
    const matchesItem = itemFilter === 'all' || item.id === itemFilter;
    
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesLocation && matchesItemGroup && matchesItem && matchesSearch;
  });

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleLocationFilterChange = (event: SelectChangeEvent) => {
    setLocationFilter(event.target.value);
    setPage(0);
  };

  const handleItemGroupFilterChange = (event: SelectChangeEvent) => {
    setItemGroupFilter(event.target.value);
    setPage(0);
  };
  
  const handleItemFilterChange = (event: SelectChangeEvent) => {
    setItemFilter(event.target.value);
    setPage(0);
  };
  
  const handleRefresh = () => {
    setLoading(true);
    loadItems();
  };
  
  // Export to CSV
  const handleExportCSV = () => {
    // Create headers
    const headers = [
      'Item Code',
      'Item Name',
      'Item Group',
      'Total Quantity',
      'Reorder Level',
      'Unit',
      'Status'
    ];
    
    // Add location headers
    locations.forEach(loc => {
      headers.push(loc.name);
    });
    
    // Create rows
    const rows = filteredItems.map(item => {
      const row = [
        item.itemCode,
        item.itemName,
        item.itemGroup,
        item.totalQuantity.toString(),
        item.reorderLevel?.toString() || '-',
        item.unit,
        item.status
      ];
      
      // Add quantities for each location
      locations.forEach(loc => {
        const locationData = item.locations.find(l => l.locationId === loc.id);
        row.push(locationData ? locationData.quantity.toString() : '0');
      });
      
      return row;
    });
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\r\n');
    
    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    link.setAttribute('download', `StockBalanceReport_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Stock': return 'success';
      case 'Low Stock': return 'warning';
      case 'Out of Stock': return 'error';
      default: return 'default';
    }
  };

  const getLocationQuantity = (item: StockItem, locationId: string) => {
    if (locationId === 'all') return item.totalQuantity;
    const location = item.locations.find(loc => loc.locationId === locationId);
    return location ? location.quantity : 0;
  };
  
  // Download PDF report
  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current) return;
    
    try {
      // Save current pagination state
      let originalPage = page;
      let originalRowsPerPage = rowsPerPage;
      
      if (includeAllData) {
        // Temporarily show all data for PDF generation
        setRowsPerPage(filteredItems.length);
        setPage(0);
        
        // Wait for re-render
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Create PDF with header
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Use hospital info from API or fallback to default
      const pdfHospitalInfo = hospitalInfo || {
        name: "Airyaman Healthcare",
        address: "123 Healthcare Street, Medical District, City - 123456",
        phoneNumber: "+91 12345 67890",
        email: "info@airyaman.com"
      };
      
      // Try to add logo if available
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "Anonymous";
        logoImg.src = '/logo.png';
        
        await new Promise((resolve) => {
          logoImg.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(logoImg, 0, 0);
            const logoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            pdf.addImage(logoDataUrl, 'JPEG', 20, 10, 16, 16);
            resolve(true);
          };
          logoImg.onerror = () => {
            console.log('Logo not found, skipping...');
            resolve(false);
          };
        });
      } catch (error) {
        console.log('Error adding logo:', error);
      }
      
      // Hospital name and info
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(pdfHospitalInfo.name, 40, 20);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const address = pdfHospitalInfo.address || 'Address not available';
      const phone = pdfHospitalInfo.phoneNumber || 'Phone not available';
      const email = pdfHospitalInfo.email || 'Email not available';
      
      pdf.text(address, 40, 28);
      pdf.text(`Phone: ${phone}`, 40, 35);
      pdf.text(`Email: ${email}`, 40, 42);
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Stock Balance Report', 20, 65);
      
      // Date
      const today = new Date().toLocaleDateString();
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Report Date: ${today}`, 20, 75);
      
      // Add line separator
      pdf.line(20, 80, 190, 80);
      
      // Create canvas from the report content
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      });
      
      // Reset pagination if needed
      if (includeAllData) {
        setRowsPerPage(originalRowsPerPage);
        setPage(originalPage);
      }
      
      // Add report content to PDF
      const imgData = canvas.toDataURL('image/png');
      
      // Calculate dimensions to fit A4
      const imgWidth = 170;
      const pageHeight = 297;
      const imgHeight = canvas.height * imgWidth / canvas.width;
      let heightLeft = imgHeight;
      
      const startY = 85; // Start below the header
      let position = startY;
      
      // Add first page content
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(imgHeight, pageHeight));
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to subsequent pages
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Stock Balance Report (Continued)', 20, 20);
        pdf.line(20, 25, 190, 25);
        
        position = heightLeft - imgHeight + 30;
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(heightLeft, 240));
        heightLeft -= 240;
      }
      
      // Generate filename
      const dataType = includeAllData ? 'Complete' : 'CurrentPage';
      const filename = `StockBalanceReport_${dataType}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      
      // Download the PDF
      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      enqueueSnackbar('Error generating PDF. Please try again.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Stock Balance Report
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        View current stock levels across all locations
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search by Item Name/Code"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Location</InputLabel>
                <Select
                  value={locationFilter}
                  label="Location"
                  onChange={handleLocationFilterChange}
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Item</InputLabel>
                <Select
                  value={itemFilter}
                  label="Item"
                  onChange={handleItemFilterChange}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  {items.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.itemName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Item Group</InputLabel>
                <Select
                  value={itemGroupFilter}
                  label="Item Group"
                  onChange={handleItemGroupFilterChange}
                >
                  {itemGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  fullWidth
                  startIcon={<FileDownloadIcon />}
                  disabled={loading || filteredItems.length === 0}
                  onClick={handleExportCSV}
                >
                  CSV
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  startIcon={<PictureAsPdfIcon />}
                  endIcon={<ArrowDropDownIcon />}
                  disabled={loading || filteredItems.length === 0}
                  onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                >
                  PDF
                </Button>
                <Menu
                  anchorEl={pdfMenuAnchor}
                  open={Boolean(pdfMenuAnchor)}
                  onClose={() => setPdfMenuAnchor(null)}
                >
                  <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
                    <ListItemIcon>
                      <PictureAsPdfIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Current Page Only" />
                  </MenuItem>
                  <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
                    <ListItemIcon>
                      <PictureAsPdfIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Complete Report" />
                  </MenuItem>
                </Menu>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshIcon />
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden' }} ref={reportRef}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="subtitle1" color="text.secondary">
              No stock items found. Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Item Group</TableCell>
                  {locationFilter === 'all' ? (
                    locations.map(loc => (
                      <TableCell key={loc.id}>{loc.name}</TableCell>
                    ))
                  ) : (
                    <TableCell>{locations.find(loc => loc.id === locationFilter)?.name || 'Unknown Location'}</TableCell>
                  )}
                  <TableCell>Total</TableCell>
                  <TableCell>Reorder Level</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.itemGroup}</TableCell>
                      {locationFilter === 'all' ? (
                        locations.map(loc => {
                          const locData = item.locations.find(l => l.locationId === loc.id);
                          // Safely get quantity and reorderLevel with defaults
                          const quantity = locData?.quantity ?? 0;
                          const reorderLevel = locData?.reorderLevel ?? 0;
                          // Only show warning if we have a valid reorder level and quantity is at or below it
                          const showWarning = reorderLevel > 0 && quantity <= reorderLevel && quantity > 0;
                          return (
                            <TableCell key={loc.id}>
                              {quantity}
                              {showWarning && (
                                <Tooltip title={`Reorder Level: ${reorderLevel}`}>
                                  <Chip 
                                    label="!" 
                                    color="warning" 
                                    size="small" 
                                    sx={{ ml: 1 }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                          );
                        })
                      ) : (
                        <TableCell>
                          {getLocationQuantity(item, locationFilter)}
                          {item.status === 'Low Stock' && (
                            <Tooltip title="Low Stock">
                              <Chip 
                                label="!" 
                                color="warning" 
                                size="small" 
                                sx={{ ml: 1 }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                      <TableCell>{item.totalQuantity}</TableCell>
                      <TableCell>
                        {item.reorderLevel || '-'}
                        {item.totalQuantity <= (item.reorderLevel || 0) && item.totalQuantity > 0 && (
                          <Tooltip title="Below Reorder Level">
                            <Chip 
                              label="!" 
                              color="warning" 
                              size="small" 
                              sx={{ ml: 1 }}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>{item.unit}</TableCell>
                      <TableCell>
                        <Chip 
                          label={item.status} 
                          color={getStatusColor(item.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(item.lastUpdated).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default StockBalanceReport;
